import { useState, useEffect } from "react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { useMapStore } from "store/useMapStore";
import { usePopupStore } from "store/usePopupStore";
import { DeliveryData } from "./SearchSidebar";
import { deliveryStatusList, driverTaskList } from "./deliveryStatus";

interface SearchListItemProps {
    data: DeliveryData;
    type: number;
    setDetailOn: (idx: number, blNo: string) => void;
    index: number;
}

export default function SearchListItem({ data, type, setDetailOn, index }: SearchListItemProps) {
    const { setMapCenter, setMapZoom, setMarkers, markersData } = useMapStore();
    const { setConfirmPop } = usePopupStore();
    const [tagColor, setTagColor] = useState<string | null>(null);
    const boxStyle = "p-[12px_18px] flex flex-col gap-[4px] border-b border-[#E1E2E4] cursor-pointer hover:bg-[#F7F7F8]";
    const type1BoxStyle = twMerge(
        boxStyle,
        " p-[8px_12px_8px_18px] relative after:absolute after:content-[''] after:w-[6px] after:h-full after:inset-y-0 after:left-0 after:bg-[var(--bg-color)]",
    );

    //type 변경시 tabColor 초기화
    useEffect(() => {
        setTagColor(null);
    }, [type]);

    //data 값 체크
    useEffect(() => {
        if (data.oKind) {
            const kind = data.oKind;

            const statusColors: Record<number, string> = {
                1: "#5A5C63", // 오더접수
                2: "#FF9200", // 픽업예약
                3: "#FF7B2E", // 픽업완료
                4: "#009632", // 배송중
                5: "#1A75FF", // 배송완료
                6: "#C2C4C8", // 접수취소
            };

            // 상태 번호 찾기
            const num = deliveryStatusList.find(item => item.name === kind)?.num;

            setTagColor(num ? statusColors[num] : null);
        }
        if (data.d_kind) {
            const kind = Number(data.d_kind);

            const statusColors: Record<number, string> = {
                3: "#6541F2", // 픽업담당
                2: "#F553DA", // 배송담당
            };

            // 상태 번호 찾기
            const num = driverTaskList.find(item => item.num === kind)?.num;

            setTagColor(num ? statusColors[num] : null);
        }
    }, [data.oKind, data.d_kind]);

    //지도중심위치, 확대, 마커상세정보 띄우기 함수
    const handleUpdateMapView = (index: number | string, y?: string | null, x?: string | null) => {
        if (y && x) {
            setMapCenter({ lat: Number(y), lng: Number(x) });
            setMapZoom(17); //50m

            const updatedMarkers = markersData.map(item => ({
                ...item,
                isOpen: item.index === index ? !item.isOpen : false, // 해당 마커는 토글, 나머지는 false
            }));
            setMarkers(updatedMarkers);
        } else {
            setConfirmPop(true, "알림", "해당 담당 기사의 위치를 표시할 수 없습니다.", 1);
        }
    };

    return (
        <>
            {tagColor && data && (
                <>
                    {type === 0 && ( // 운송장 일때
                        <div
                            className={type1BoxStyle}
                            style={{ "--bg-color": tagColor } as React.CSSProperties}
                            onClick={() => {
                                setDetailOn(index, data.s_bl_no || "");
                            }}
                        >
                            <div className="flex items-center gap-[8px]">
                                <div
                                    className="text-[12px] font-[700] text-white p-[4px_8px] rounded-[8px]"
                                    style={{ backgroundColor: tagColor }}
                                >
                                    {data.oKind}
                                </div>
                                <p className="text-[12px] text-[#878A93]">{moment(data.o_date).format("YY.MM.DD HH:mm:ss")}</p>
                            </div>
                            <div className="flex items-center gap-[4px]">
                                <p className="text-[18px] font-[700]">{data.s_bl_no}</p>
                                <p className="text-[15px] text-[#5A5C63] leading-[1]">{data.sKindName}</p>
                            </div>
                            <ul className="flex flex-col gap-[4px]">
                                <li className="flex">
                                    <p className="w-[40px] text-[14px] text-[#878A93]">픽업</p>
                                    <div className="flex-1 flex flex-wrap items-center gap-[4px] text-[13px]">
                                        <button
                                            type="button"
                                            className="text-[13px] text-left"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleUpdateMapView(`${index}_0`, data.S_C_y, data.S_C_x);
                                            }}
                                        >
                                            <span className="text-[14px] font-[500] underline">{data.S_C_Contact}</span>
                                            <span className="pl-[4px]">{data.cKind}</span>
                                        </button>
                                        {data.pickupName && (
                                            <button
                                                type="button"
                                                className={`text-[13px] text-[#5A5C63] underline text-left p-[1px_24px_1px_0] bg-no-repeat bg-[length:21px_21px] bg-right ${
                                                    data.pickup_y ? "bg-pin_p_on" : "bg-pin"
                                                }`}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    handleUpdateMapView(`${index}_1`, data.pickup_y, data.pickup_x);
                                                }}
                                            >
                                                담당기사 {data.pickupName}
                                            </button>
                                        )}
                                    </div>
                                </li>
                                <li className="flex">
                                    <p className="w-[40px] text-[14px] text-[#878A93]">배송</p>
                                    <div className="flex-1 flex flex-wrap items-center gap-[4px] text-[13px]">
                                        <button
                                            type="button"
                                            className="text-[13px] text-left"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleUpdateMapView(`${index}_2`, data.S_B_y, data.S_B_x);
                                            }}
                                        >
                                            <span className="text-[14px] font-[500] underline">{data.S_B_Contact}</span>
                                            <span className="pl-[4px]">{data.bKind}</span>
                                        </button>
                                        {data.bpickupName && (
                                            <button
                                                type="button"
                                                className={`text-[13px] text-[#5A5C63] underline text-left p-[1px_24px_1px_0] bg-no-repeat bg-[length:21px_21px] bg-right ${
                                                    data.bpickup_y ? "bg-pin_d_on" : "bg-pin"
                                                }`}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    handleUpdateMapView(`${index}_3`, data.bpickup_y, data.bpickup_x);
                                                }}
                                            >
                                                담당기사 {data.bpickupName}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    )}
                    {type === 1 && ( // 담당기사 일때
                        <div
                            className={boxStyle}
                            onClick={() => {
                                handleUpdateMapView(index, data.y_location, data.x_location);
                            }}
                        >
                            <div className="flex items-center gap-[8px]">
                                <div
                                    className="text-[12px] font-[700] text-white p-[4px_8px] rounded-[8px]"
                                    style={{ backgroundColor: tagColor }}
                                >
                                    {data.d_kind_name}
                                </div>
                                <p className="text-[12px] text-[#878A93]">{data.reg_date !== "Invalid date" ? data.reg_date : "-"}</p>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-[4px]">
                                    <p className="text-[18px] font-[700]">{data.d_name}</p>
                                    <p className="text-[15px] text-[#5A5C63] leading-[1]">{data.d_e_tel}</p>
                                </div>
                                <div
                                    className={`w-[21px] h-[21px] bg-no-repeat bg-[length:21px_21px] bg-right ${
                                        data.d_kind === "3" && data.y_location
                                            ? "bg-pin_p_on"
                                            : data.d_kind === "4" && data.y_location
                                              ? "bg-pin_d_on"
                                              : "bg-pin"
                                    }`}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
